// src/components/UI/CreditsExhaustedModal.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './CreditsExhaustedModal.module.css';
import insufficientCreditsImg from '../../assets/dashboard_assets/insufficient_credits.png';

/**
 * Low-credits popup. Two variants:
 *  - Balance warning (default): reports the exact balance left, or reads as fully
 *    exhausted at zero.
 *  - `insufficient`: shown when a feature is attempted without enough credits for it;
 *    uses the original exhausted-style copy worded as "insufficient credits".
 * "Buy Credits" routes to the plans page; dismissible via close.
 * `connectAmazon` overrides both: new users get their free credits on first Amazon
 * connect, so they're prompted to connect instead of told they're out of credits.
 */
const CreditsExhaustedModal = ({ show, onClose, onBuyCredits, credits, insufficient = false, connectAmazon = false, connectRewardCredits, onConnectAmazon }) => {
    const navigate = useNavigate();

    if (!show) return null;

    const hasSomeCredits = typeof credits === 'number' && credits > 0;
    const plural = (n) => `credit${n === 1 ? '' : 's'}`;

    const handleBuyCredits = async () => {
        // Run any pending action (e.g. save draft) before leaving the page.
        if (typeof onBuyCredits === 'function') {
            try { await onBuyCredits(); } catch (e) { /* don't block navigation on save failure */ }
        }
        onClose?.();
        navigate('/dashboard/plans');
    };

    // Copy and CTA are resolved together so the connect wording can never be
    // paired with "Buy Credits" (or vice versa).
    let variant;
    if (connectAmazon) {
        // Reward amount comes from /auth/me; fall back to generic wording if it hasn't landed.
        const reward = typeof connectRewardCredits === 'number' ? `${connectRewardCredits} ` : '';
        const balance = hasSomeCredits ? credits : 0;
        variant = {
            title: `Get ${reward}free credits`,
            subtitle: `You currently have ${balance} ${plural(balance)}. Connect your Amazon Seller account to claim your ${reward}free credits and start creating listings.`,
            ctaLabel: 'Connect Amazon',
            onCta: onConnectAmazon,
        };
    } else {
        variant = {
            title: insufficient
                ? 'Oops...You have insufficient credits!'
                : hasSomeCredits
                    ? `You have ${credits} ${plural(credits)} left!`
                    : 'Oops...You have exhausted all your credits!',
            subtitle: 'To continue usage upgrade your plan or buy more credits.',
            ctaLabel: 'Buy Credits',
            onCta: handleBuyCredits,
        };
    }

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.popup} onClick={(e) => e.stopPropagation()}>
                <button className={styles.closeButton} onClick={onClose} aria-label="Close">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M16 1.4L14.6 0L8 6.6L1.4 0L0 1.4L6.6 8L0 14.6L1.4 16L8 9.4L14.6 16L16 14.6L9.4 8L16 1.4Z" fill="#505050" />
                    </svg>
                </button>

                <div className={styles.content}>
                    {/* Insufficient credits illustration */}
                    <img
                        className={styles.iconImage}
                        src={insufficientCreditsImg}
                        alt="Insufficient credits"
                    />

                    <div className={styles.textBlock}>
                        <div className={styles.textGroup}>
                            <h2 className={styles.title}>{variant.title}</h2>
                            <p className={styles.subtitle}>{variant.subtitle}</p>
                        </div>

                        <button className={styles.buyButton} onClick={variant.onCta}>
                            {variant.ctaLabel}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreditsExhaustedModal;
